/**
 * Hand-drawn line-art icon set.
 * One visual language: 24x24 box, 1.5 stroke, round caps, currentColor.
 * Decorative by default (aria-hidden) — labels always sit beside them.
 */

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
};

const paths = {
  // — Treatments —
  joint: (
    <>
      <path d="M7 3v5.2a3.8 3.8 0 0 0 1.4 2.9l1.2 1a3.8 3.8 0 0 1 1.4 2.9V21" />
      <path d="M17 3v4.4a3.8 3.8 0 0 1-1.4 2.9l-1.2 1a3.8 3.8 0 0 0-1.4 2.9V21" />
      <circle cx="12" cy="12" r="2.4" />
    </>
  ),
  neuro: (
    <>
      <path d="M12 4.5v15" />
      <path d="M12 8c-1.9 0-3.4-1-4.6-2.1M12 8c1.9 0 3.4-1 4.6-2.1" />
      <path d="M12 13c-2.4 0-4.3-1.1-5.9-2.6M12 13c2.4 0 4.3-1.1 5.9-2.6" />
      <path d="M12 18c-1.9 0-3.5-.8-4.9-2M12 18c1.9 0 3.5-.8 4.9-2" />
      <circle cx="12" cy="3.6" r="1.3" />
    </>
  ),
  surgical: (
    <>
      <path d="M4 20 14.5 9.5" />
      <path d="M12.4 7.4 16 3.8a2.6 2.6 0 0 1 3.7 3.7l-3.6 3.6" />
      <path d="m11.6 12.4 3.4 3.4" />
      <path d="M5.2 18.8 3.5 20.5" />
      <circle cx="18.6" cy="17.4" r="2.6" />
    </>
  ),
  spine: (
    <>
      <path d="M12 3v18" />
      <path d="M8.5 5.5h7M8 9.3h8M8 13.1h8M8.5 16.9h7" />
    </>
  ),

  // — Advanced recovery —
  cupping: (
    <>
      <path d="M6.5 4h11l-1 8.4a4.6 4.6 0 0 1-4.5 4.1h0a4.6 4.6 0 0 1-4.5-4.1Z" />
      <path d="M12 16.5V21" />
      <path d="M9 21h6" />
      <path d="M9.6 7.4h4.8" />
    </>
  ),
  needle: (
    <>
      <path d="M20.5 3.5 9.8 14.2" />
      <path d="m17.6 3 3.4 3.4" />
      <path d="M9.8 14.2 7 20.4a.6.6 0 0 1-1.1.1L3.6 16" />
      <path d="M3.6 16 9.8 14.2" />
    </>
  ),
  iastm: (
    <>
      <path d="M3.6 14.6c3.2-3.4 6.7-3.4 9.9 0" />
      <path d="M15.4 4.6a2.2 2.2 0 0 1 3.1 0l1 1a2.2 2.2 0 0 1 0 3.1l-6.2 6.2-4.1 1 1-4.1Z" />
      <path d="M4 19.6h16" />
    </>
  ),

  // — Physiotherapy specialisms —
  geriatric: (
    <>
      <circle cx="10.5" cy="4.2" r="1.8" />
      <path d="M10.5 6.4v4.2l-2.4 3.1 1.4 6.9" />
      <path d="m8.1 13.7-2.6 6.7" />
      <path d="M10.5 8.4 13.6 10" />
      <path d="M17.6 8.6v12.2" />
      <path d="M15.9 20.8h3.4" />
    </>
  ),
  posture: (
    <>
      <circle cx="9.4" cy="4.4" r="1.9" />
      <path d="M9.4 6.6v5.6h5.1" />
      <path d="M9.4 12.2H5.9v5.4" />
      <path d="M5.9 17.6h4.6" />
      <path d="M18.6 3.4v17.2" />
      <path d="M16.4 5.6c1.5.9 1.5 2.6 0 3.5" />
    </>
  ),
  vestibular: (
    <>
      <path d="M8.6 20.4c-2.8-1.4-4.6-4.3-4.6-7.6a8.4 8.4 0 0 1 16.8 0" />
      <path d="M12.4 16.8a4 4 0 1 1 3.6-4" />
      <circle cx="12.4" cy="12.6" r="1.1" fill="currentColor" stroke="none" />
      <path d="M17.6 18.4h4M19.6 16.4v4" />
    </>
  ),
  strength: (
    <>
      <path d="M3 9.4v5.2M6 7.4v9.2M21 9.4v5.2M18 7.4v9.2" />
      <path d="M6 12h12" />
    </>
  ),

  // — Fitness & training —
  bodyweight: (
    <>
      <circle cx="12" cy="4.6" r="1.9" />
      <path d="M12 7v6" />
      <path d="M12 8.6 6.8 11M12 8.6l5.2 2.4" />
      <path d="M12 13 8.4 20M12 13l3.6 7" />
    </>
  ),
  functional: (
    <>
      <path d="M3 17.5h4l2.6-9 3 12L15 12h6" />
      <circle cx="6.5" cy="5.5" r="1.8" />
    </>
  ),
  hiit: (
    <>
      <path d="M13.2 2.5 5 13.4h5.6L9.8 21.5 18.2 10h-5.7Z" />
    </>
  ),
  zumba: (
    <>
      <circle cx="14.6" cy="4.4" r="1.8" />
      <path d="m14 7.6-3.6 3 2.6 2.6-1 7.6" />
      <path d="m13 13.2 4 2.4" />
      <path d="M10.4 10.6 5.8 9.2" />
      <path d="m11.6 20.8-3.4-4.2" />
    </>
  ),
  cardio: (
    <>
      <path d="M3 12.4h3.6l1.7-3.6 2.7 7.4 2.3-5.2 1.5 2.6h6" />
      <path d="M20.4 8.2a3.6 3.6 0 0 0-5.1-4.6L12 6.3 8.7 3.6A3.6 3.6 0 0 0 3.6 8.2" />
    </>
  ),
  yoga: (
    <>
      <circle cx="12" cy="4.4" r="1.9" />
      <path d="M12 6.8v5.4" />
      <path d="M4.4 10.4 12 12.2l7.6-1.8" />
      <path d="M12 12.2 8 19.4h8Z" />
    </>
  ),
  taichi: (
    <>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M12 3.2a4.4 4.4 0 0 0 0 8.8 4.4 4.4 0 0 1 0 8.8" />
      <circle cx="12" cy="7.6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="16.4" r="0.9" />
    </>
  ),
  qigong: (
    <>
      <path d="M4 18.4c2.6 0 2.6-2.8 5.2-2.8s2.6 2.8 5.2 2.8 2.6-2.8 5.2-2.8" />
      <path d="M4 13c2.6 0 2.6-2.8 5.2-2.8s2.6 2.8 5.2 2.8S17 10.2 19.6 10.2" />
      <path d="M4 7.6c2.6 0 2.6-2.8 5.2-2.8S11.8 7.6 14.4 7.6s2.6-2.8 5.2-2.8" />
    </>
  ),

  // — Oriental & alternative —
  aroma: (
    <>
      <path d="M12 21c3.6 0 6.4-2.7 6.4-6.2 0-4-3.4-6.6-6.4-11.3C9 8.2 5.6 10.8 5.6 14.8 5.6 18.3 8.4 21 12 21Z" />
      <path d="M12 17.4a2.8 2.8 0 0 1-2.7-2.9" />
    </>
  ),
  moxa: (
    <>
      <path d="M12 20.6a5 5 0 0 0 5-5c0-3.4-3.2-4.6-2.4-8.4C12 8.6 11 10.6 11 12.2c-1-.6-1.4-1.8-1.4-3-1.6 1.6-2.6 3.8-2.6 6.4a5 5 0 0 0 5 5Z" />
      <path d="M8 22.4h8" />
    </>
  ),
  cranio: (
    <>
      <path d="M17.6 10.6a5.8 5.8 0 1 0-9.9 4.1v2.8h6.6v-2a3.4 3.4 0 0 1 1.3-2.6" />
      <path d="M7.7 17.5v2.9" />
      <path d="M14.3 17.5v2.9" />
      <path d="M20 6.4c1.2 1 1.2 2.8 0 3.8" />
    </>
  ),
  microcurrent: (
    <>
      <path d="M13.6 2.6 5.8 13h5.4l-1 8.4L18 11h-5.4Z" />
      <path d="M2.6 8.4h2.8M18.6 15.6h2.8" />
    </>
  ),

  // WhatsApp brand mark — filled (the official silhouette reads far
  // cleaner at small sizes than a hand-drawn stroke approximation)
  whatsapp: (
    <path
      fill="currentColor"
      stroke="none"
      d="M18.4 5.6A9.9 9.9 0 0 0 2.7 17.4L1.5 22l4.7-1.2a9.9 9.9 0 0 0 4.7 1.2h.01A9.9 9.9 0 0 0 18.4 5.6ZM10.9 20.2h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 1 1 15.28-4.35 8.19 8.19 0 0 1-8.29 8.21Zm4.51-6.16c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.13-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42h-.47c-.16 0-.43.06-.65.31-.23.25-.86.84-.86 2.06s.88 2.39 1 2.56c.13.16 1.74 2.65 4.21 3.72.59.25 1.05.4 1.4.52.59.19 1.13.16 1.55.1.47-.07 1.46-.6 1.67-1.18.2-.57.2-1.06.14-1.16-.06-.1-.22-.16-.47-.28Z"
    />
  ),

  // — UI —
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  mail: (
    <>
      <rect x="2.8" y="4.8" width="18.4" height="14.4" rx="2.2" />
      <path d="m3.4 6.6 8.6 6 8.6-6" />
    </>
  ),
  phone: (
    <path d="M8.4 3.6H5.6A2.2 2.2 0 0 0 3.4 6c.5 8 6.6 14.1 14.6 14.6a2.2 2.2 0 0 0 2.4-2.2v-2.8l-4.2-1.4-2 2a14.6 14.6 0 0 1-6.4-6.4l2-2Z" />
  ),
  pin: (
    <>
      <path d="M12 21.4c4-4.3 6.2-7.6 6.2-10.4a6.2 6.2 0 1 0-12.4 0c0 2.8 2.2 6.1 6.2 10.4Z" />
      <circle cx="12" cy="10.8" r="2.4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M12 6.8V12l3.4 2" />
    </>
  ),
  arrow: <path d="M4.5 12h15m-6-6 6 6-6 6" />,
  chevron: <path d="m6 9.5 6 6 6-6" />,
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
};

export default function Icon({ name, size = 24, className, ...rest }) {
  const glyph = paths[name];
  if (!glyph) return null;

  return (
    <svg {...base} width={size} height={size} className={className} {...rest}>
      {glyph}
    </svg>
  );
}
